import type { FC, PropsWithChildren } from 'react';
import React, { Children, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import cn from 'classnames';

import type { TMarkdownContainer, TAllowedHeading } from '../models';
import { mutate } from '../helpers/mutate-html';
import type { TMarkdownComponents } from '../models/markdown-types';

export const MarkdownContainer: FC<React.PropsWithChildren<TMarkdownContainer>> = ({
    Block,
    Hr,
    Img,
    Link,
    List,
    Text,
    config,
    content,
    htmlContent = '',
    linkTargetBlank = false,
    pluggins,
    renderers,
    styles,
}) => {
    const htmlContentMutated = useMemo(() => {
        return mutate(htmlContent || '');
    }, [htmlContent]);

    const paragraph = ({ children }: PropsWithChildren) => (
        <Text
            className={cn(styles.text, styles.external)}
            styler={{ marginTop: 0, marginBottom: 12 }}
            tag="p"
        >
            {children}
        </Text>
    );

    const heading: TMarkdownComponents<TAllowedHeading> = ({ children, level }) => {
        const tag = `h${level}` as TAllowedHeading;

        return (
            <Text
                styler={{ marginTop: config[tag].mt, marginBottom: config[tag].mb }}
                tag={tag}
                view={config[tag].view}
            >
                {children}
            </Text>
        );
    };

    const link: TMarkdownComponents<'a'> = ({ children, href }) => (
        <Link
            className={cn(styles.linkColor, styles.external)}
            href={href}
            {...(linkTargetBlank ? { target: '_blank' } : {})}
        >
            {children}
        </Link>
    );

    const image: TMarkdownComponents<'img'> = ({ alt, src }) => (
        <Img
            alt={alt}
            className={cn(styles.img, styles.center)}
            src={src}
            styler={{
                marginTop: config.img.mt,
                marginBottom: config.img.mb,
            }}
        />
    );

    const strong: TMarkdownComponents<'strong'> = ({ children }) => (
        <Text fontWeight={6} tag="span">
            {children}
        </Text>
    );

    const thematicBreak: TMarkdownComponents<'hr'> = () => (
        <Hr
            styler={{
                marginTop: config.hr.mt,
                marginBottom: config.hr.mb,
            }}
        />
    );

    const listItem: TMarkdownComponents<'li'> = props => paragraph(props);

    const unorderedList: TMarkdownComponents<'ul'> = ({ children }) => {
        const stylesForListInMarkdown = cn(styles.external, styles.listMarkdown);
        const filteredChildren = Children.toArray(children).filter(child => child !== '\n');

        return (
            <List className={stylesForListInMarkdown} indentType="none" marker="•" type="ul">
                {filteredChildren}
            </List>
        );
    };

    const orderedList: TMarkdownComponents<'ol'> = ({ children }) => {
        const stylesForListInMarkdown = cn(styles.external, styles.listMarkdown);
        const filteredChildren = Children.toArray(children).filter(child => child !== '\n');

        return (
            <List className={stylesForListInMarkdown} marker="" olRedMarker olType="1" type="ol">
                {filteredChildren}
            </List>
        );
    };

    const blockquote: TMarkdownComponents<'blockquote'> = ({ children }) => {
        return (
            <Block
                className={cn(styles.quote, styles.external)}
                notPadded
                styler={{
                    paddingBottom: config.blockquote.pb,
                    paddingTop: config.blockquote.pt,
                    paddingRight: config.blockquote.pt,
                    paddingLeft: config.blockquote.pt,
                    marginBottom: config.blockquote.mb,
                    marginTop: config.blockquote.mt,
                }}
            >
                {children}
            </Block>
        );
    };

    return (
        <ReactMarkdown
            components={{
                a: link,
                blockquote: blockquote,
                h1: heading,
                h2: heading,
                h3: heading,
                hr: thematicBreak,
                img: image,
                li: listItem,
                ol: orderedList,
                p: paragraph,
                ul: unorderedList,
                strong: strong,
                ...renderers,
            }}
            {...pluggins}
        >
            {(htmlContentMutated || content) ?? ''}
        </ReactMarkdown>
    );
};
